import { useEffect, useReducer } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { getFirst } from '../outline/utils.js'
import { MilestoneRow } from './ScheduleSection.jsx'
import { ItemNode } from './MilestoneSections.jsx'

/* ── Highlight matched text ─────────────────────────────────────────────── */
function Highlight({ text, query }) {
  if (!query || !text) return <>{text || ''}</>
  const qe = query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
  const parts = text.split(new RegExp('(' + qe + ')', 'gi'))
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <mark key={i} style={{ background: '#fff3a3', borderRadius: 2, padding: '0 1px' }}>{part}</mark>
          : part
      )}
    </>
  )
}

/* ── Project result row ─────────────────────────────────────────────────── */
function ProjectResultNode({ proj, query }) {
  const name   = proj.fields['Project Name']   || 'Unnamed Project'
  const number = proj.fields['Project Number'] || ''
  return (
    <div className="wf-node wf-node--project">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <button
          className="wf-bullet wf-bullet--project"
          title="Open project"
          data-fn="zoomTo"
          data-level="project"
          data-projid={proj.id}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--project">
            {number && (
              <span className="wf-proj-number">
                <Highlight text={number} query={query} />
              </span>
            )}
            <Highlight text={name} query={query} />
          </span>
        </div>
      </div>
    </div>
  )
}

/* ── Sheet result row ────────────────────────────────────────────────────── */
function SheetResultNode({ sheet, query }) {
  const sv = sheet.fields['Sheet Number and Title (from Drawing Sheet)'] || ''
  const title = String(Array.isArray(sv) ? (sv[0] || '') : (sv || '')).trim() || 'Untitled'
  return (
    <div className="wf-node wf-node--sheet">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--sheet">
            <Highlight text={title} query={query} />
          </span>
        </div>
      </div>
    </div>
  )
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function getMsForPhase(ph, milestones) {
  const pname = ph.fields['Phase Name'] || ''
  return milestones.filter(m => (m.fields['Project Phase'] || '') === pname)
}

function buildMilestoneEntries(scopedMilestones, qLower) {
  const entries = []
  for (const { ms, phId, phName, projName } of scopedMilestones) {
    const msId   = ms.id
    const msName = ms.fields['Full Deliverable Name'] || ms.fields['Name'] || ''
    const msMatch = msName.toLowerCase().includes(qLower)

    /* Matched checklist items */
    const rawItems     = ctx.clCache?.[msId] || []
    const allProcessed = rawItems.length > 0
      ? (ctx.getCategoryNodes(msId, rawItems) || []).flatMap(c => c.items)
      : []
    const matchedItems = allProcessed.filter(item => {
      const iname = getFirst(item.fields['Name for List (from Deliverable Checklist Item Template)']) || ''
      return iname.toLowerCase().includes(qLower)
    })

    /* Matched drawing sheets */
    const matchedSheets = (ctx.sheetCache?.[msId] || []).filter(sheet => {
      const sv    = sheet.fields['Sheet Number and Title (from Drawing Sheet)'] || ''
      const sname = String(Array.isArray(sv) ? (sv[0] || '') : (sv || '')).trim()
      return sname && sname.toLowerCase().includes(qLower)
    })

    if (!msMatch && !matchedItems.length && !matchedSheets.length) continue
    entries.push({ ms, phId, phName, projName, msName, msMatch, matchedItems, matchedSheets })
  }
  return entries
}

/* ── Main component ──────────────────────────────────────────────────────── */
export default function SearchResults() {
  const [, forceUpdate] = useReducer(v => v + 1, 0)

  useEffect(() => {
    function onRender() { forceUpdate() }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  const q = (wf.searchQuery || '').trim()
  if (!q) return null

  const qLower = q.toLowerCase()

  /* ── Top-level search (no active project) ─────────────────────────────── */
  if (!wf.zoomProjectId) {
    const projects = ctx.projectsCache || []

    /* Project name / number matches */
    const projMatches = projects.filter(proj => {
      const name   = (proj.fields['Project Name']   || '').toLowerCase()
      const number = (proj.fields['Project Number'] || '').toLowerCase()
      return name.includes(qLower) || number.includes(qLower)
    })

    /* Milestone / item / sheet matches across all loaded project caches */
    const scopedMilestones = []
    for (const proj of projects) {
      const projPhases = ctx.phasesCache?.[proj.id]
      const projMs     = ctx.milestonesCache?.[proj.id]
      if (!projPhases || !projMs) continue  // not yet loaded — skip
      const projName = proj.fields['Project Name'] || ''
      projPhases.forEach(ph => {
        getMsForPhase(ph, projMs).forEach(ms =>
          scopedMilestones.push({ ms, phId: ph.id, phName: ph.fields['Phase Name'] || '', projName })
        )
      })
    }
    const msEntries = buildMilestoneEntries(scopedMilestones, qLower)

    if (!projMatches.length && !msEntries.length) {
      return (
        <div style={{ padding: '24px 0', textAlign: 'center', fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: '#7a9aaa' }}>
          No results for &ldquo;{q}&rdquo;
        </div>
      )
    }

    return (
      <div className="wf-list">
        {/* Project rows */}
        {projMatches.map(proj => (
          <ProjectResultNode key={proj.id} proj={proj} query={q} />
        ))}

        {/* Milestone / item / sheet results across loaded projects */}
        {msEntries.map(({ ms, phId, phName, projName, msName, msMatch, matchedItems, matchedSheets }) => (
          <div key={ms.id}>
            <div className="wf-search-crumb">
              {projName && <>{projName} › </>}
              {phName && <>{phName} › </>}
              <Highlight text={msName} query={q} />
            </div>
            {msMatch && <MilestoneRow ms={ms} phId={phId} />}
            {matchedItems.length > 0 && (
              <div className="wf-search-children">
                {matchedItems.map(item => (
                  <ItemNode key={item.record.id} item={item} msId={ms.id} />
                ))}
              </div>
            )}
            {matchedSheets.length > 0 && (
              <div className="wf-search-children">
                {matchedSheets.map(sheet => (
                  <SheetResultNode key={sheet.id} sheet={sheet} query={qLower} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  /* ── In-project search ────────────────────────────────────────────────── */
  const phases     = ctx.state?.phases    || []
  const milestones = ctx.state?.milestones || []

  const scopedMilestones = []
  if (wf.zoomMsId) {
    const ms = milestones.find(m => m.id === wf.zoomMsId)
    if (ms) scopedMilestones.push({ ms, phId: wf.zoomPhaseId, phName: ms.fields['Project Phase'] || '', projName: '' })
  } else if (wf.zoomPhaseId) {
    const ph = phases.find(p => p.id === wf.zoomPhaseId)
    if (ph) {
      getMsForPhase(ph, milestones).forEach(ms =>
        scopedMilestones.push({ ms, phId: wf.zoomPhaseId, phName: ph.fields['Phase Name'] || '', projName: '' })
      )
    }
  } else {
    phases.forEach(ph => {
      getMsForPhase(ph, milestones).forEach(ms =>
        scopedMilestones.push({ ms, phId: ph.id, phName: ph.fields['Phase Name'] || '', projName: '' })
      )
    })
  }

  const entries = buildMilestoneEntries(scopedMilestones, qLower)

  if (!entries.length) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center', fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: '#7a9aaa' }}>
        No results for &ldquo;{q}&rdquo;
      </div>
    )
  }

  return (
    <div className="wf-list">
      {entries.map(({ ms, phId, phName, msName, msMatch, matchedItems, matchedSheets }) => (
        <div key={ms.id}>
          {!wf.zoomMsId && (
            <div className="wf-search-crumb">
              {!wf.zoomPhaseId && phName && <>{phName} ›{' '}</>}
              <Highlight text={msName} query={q} />
            </div>
          )}
          {msMatch && <MilestoneRow ms={ms} phId={phId} />}
          {matchedItems.length > 0 && (
            <div className="wf-search-children">
              {matchedItems.map(item => (
                <ItemNode key={item.record.id} item={item} msId={ms.id} />
              ))}
            </div>
          )}
          {matchedSheets.length > 0 && (
            <div className="wf-search-children">
              {matchedSheets.map(sheet => (
                <SheetResultNode key={sheet.id} sheet={sheet} query={qLower} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
